import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Alert } from "@mui/material";
import styles from "./BrokerSelectionPage.module.css";
import CheckmarkIcon from "../../assets/SVG/CheckmarkIcon.svg";

const brokers = [
    {
        id: "zerodha",
        name: "Zerodha",
        type: "Discount Broker",
        logo: "/ZerodhaLogo.png"
    },
    {
        id: "upstox",
        name: "Upstox",
        type: "Discount Broker",
        logo: "/upstocks.png"
    },
    {
        id: "groww",
        name: "Groww",
        type: "Discount Broker",
        logo: "/groww.png"
    },
    {
        id: "angel-one",
        name: "Angel One",
        type: "Discount Broker",
        logo: "/angleOne.png"
    },
    {
        id: "icici-direct",
        name: "ICICI Direct",
        type: "Bank Based",
        logo: "/iciciDirect.png"
    },
    {
        id: "hdfc-sky",
        name: "HDFC Sky",
        type: "Bank Based",
        logo: "/hdfcSky.png"
    },
    {
        id: "kotak-sec",
        name: "Kotak Sec",
        type: "Bank Based",
        logo: "/kotakSec.png"
    },
    {
        id: "nuvama",
        name: "Nuvama",
        type: "Bank Based",
        logo: "/nuvama.png"
    }
];

function BrokerSelectionPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedBroker, setSelectedBroker] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentStep, setCurrentStep] = useState("selection"); // "selection" or "confirmation"
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");

    // Get contact details from location state or localStorage
    useEffect(() => {
        const savedEmail = location.state?.email || localStorage.getItem("user_email") || "";
        const savedPhone = location.state?.phone || localStorage.getItem("user_phone") || "";
        setEmail(savedEmail);
        setPhone(savedPhone);
    }, [location.state]);

    const filteredBrokers = brokers.filter(broker =>
        broker.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const maskPhone = (phoneNumber) => {
        if (!phoneNumber || phoneNumber.length < 4) return phoneNumber;
        const visible = phoneNumber.slice(-2);
        const masked = phoneNumber.slice(0, -2).replace(/\d/g, "*");
        return masked + visible;
    };

    const maskEmail = (emailAddress) => {
        if (!emailAddress) return "";
        const [localPart, domain] = emailAddress.split("@");
        if (!localPart || !domain) return emailAddress;

        const visibleChars = 3;
        const maskedLocal = localPart.length > visibleChars
            ? localPart.slice(0, visibleChars) + "*".repeat(localPart.length - visibleChars)
            : localPart;

        return `${maskedLocal}@${domain}`;
    };

    const handleContinue = () => {
        if (selectedBroker) {
            // Move to confirmation step
            setCurrentStep("confirmation");
        }
    };

    const handleConfirm = () => {
        // Navigate to next step (OTP verification for broker connection)
        navigate("/order-book");
    };

    const handleChangeBroker = () => {
        // Go back to selection step
        setCurrentStep("selection");
    };

    return (
        <div className={styles["container"]}>
            <div className={`${styles["content"]} ${currentStep === "confirmation" ? styles["content-narrow"] : ""}`}>
                {currentStep === "selection" ? (
                    <>
                        <h1 className={styles["title"]}>Connect Your Demat Account</h1>
                        <p className={styles["subtitle"]}>
                            Select Your Broker To verify Your Holdings and add them to your family Portfolio
                        </p>

                        {/* Search Bar */}
                        <div className={styles["search-container"]}>
                            <div className={styles["search-wrapper"]}>
                                <svg
                                    className={styles["search-icon"]}
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="m21 21-4.35-4.35"></path>
                                </svg>
                                <input
                                    type="text"
                                    className={styles["search-input"]}
                                    placeholder="Search Your Broker(e.g. Zerodha, Angel One......)"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Broker Grid */}
                        <div className={styles["broker-grid"]}>
                            {filteredBrokers.map((broker) => (
                                <div
                                    key={broker.id}
                                    className={`${styles["broker-card"]} ${selectedBroker?.id === broker.id ? styles["broker-card-selected"] : ""
                                        }`}
                                    onClick={() => setSelectedBroker(broker)}
                                >
                                    {selectedBroker?.id === broker.id && (
                                        <div className={styles["checkmark"]}>
                                            <img 
                                                src={CheckmarkIcon} 
                                                alt="Selected" 
                                                className={styles["checkmark-icon"]}
                                            />
                                        </div>
                                    )}
                                    <div className={styles["broker-logo"]}>
                                        {broker.logo.includes('.png') || broker.logo.includes('.jpg') || broker.logo.includes('.svg') ? (
                                            <img
                                                src={broker.logo}
                                                alt={broker.name}
                                                className={styles["broker-logo-image"]}
                                            />
                                        ) : (
                                            broker.logo
                                        )}
                                    </div>
                                    <div className={styles["broker-info"]}>
                                        <h3 className={styles["broker-name"]}>{broker.name}</h3>
                                        <p className={styles["broker-type"]}>{broker.type}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer Links */}
                        <div className={styles["footer-links"]}>
                            <span className={styles["footer-text"]}>Don't see your broker?</span>
                            <button className={styles["footer-link"]}>View list</button>
                            <span className={styles["footer-separator"]}>|</span>
                            <button className={styles["footer-link"]}>Contact support</button>
                        </div>

                        <div className={styles["footer-buttons"]}>
                            {/* Security Message */}
                            <p className={styles["security-message"]}>Your data is encrypted and secure</p>

                            {/* Continue Button */}
                            <button
                                className={`${styles["continue-button"]} ${!selectedBroker ? styles["continue-button-disabled"] : ""
                                    }`}
                                onClick={handleContinue}
                                disabled={!selectedBroker}
                            >
                                Continue to Proceed
                            </button>
                        </div>
                    </>
                ) : (
                    <div className={styles["confirmation-container"]}>
                        {/* Broker Logo */}
                        <div className={styles["broker-logo-container"]}>
                            {selectedBroker?.logo && (selectedBroker.logo.includes('.png') || selectedBroker.logo.includes('.jpg') || selectedBroker.logo.includes('.svg')) ? (
                                <img
                                    src={selectedBroker.logo}
                                    alt={selectedBroker.name}
                                    className={styles["broker-logo-image-large"]}
                                />
                            ) : (
                                <div className={styles["broker-logo-large"]}>
                                    {selectedBroker?.logo || "ZERODHA"}
                                </div>
                            )}
                        </div>

                        <h1 className={`${styles["title"]} ${styles["title-centered"]}`}>Confirm Details</h1>
                        <p className={`${styles["subtitle"]} ${styles["subtitle-centered"]}`}>
                            Verify the contact information to fetched from {selectedBroker?.name}
                        </p>

                        {/* Contact Details */}
                        <div className={styles["contact-details"]}>
                            {/* Phone Field */}
                            <div className={styles["contact-field"]}>
                                <div className={styles["contact-icon"]}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                    </svg>
                                </div>
                                <div className={styles["contact-value"]}>
                                    {maskPhone(phone) || "98******56"}
                                </div>
                                <div className={styles["contact-checkmark"]}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </div>
                            </div>

                            {/* Email Field */}
                            <div className={styles["contact-field"]}>
                                <div className={styles["contact-icon"]}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                </div>
                                <div className={styles["contact-value"]}>
                                    {maskEmail(email) || "123****@gmail.com"}
                                </div>
                                <div className={styles["contact-checkmark"]}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Info Alert */}
                        <Alert
                            variant="outlined"
                            severity="info"
                            sx={{
                                marginBottom: '24px',
                                borderRadius: '3.528px',
                                border: '0.882px solid var(--accent-soft-strong)',
                                backgroundColor: 'var(--color-info-bg)',
                                '& .MuiAlert-icon': {
                                    color: 'var(--accent)',
                                },
                                '& .MuiAlert-message': {
                                    color: 'var(--text-strong)',
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 400
                                }
                            }}
                        >
                            We will send One Time Password(OTP) to these contact details in the next step to verify your ownership.
                        </Alert>

                        {/* Confirm Button */}
                        <button
                            className={styles["confirm-button"]}
                            onClick={handleConfirm}
                        >
                            Confirm & Verify <span className={styles["arrow"]}>&gt;</span>
                        </button>

                        {/* Change Broker Link */}
                        <button
                            className={styles["change-broker-link"]}
                            onClick={handleChangeBroker}
                        >
                            <span className={styles["back-arrow"]}>&lt;</span> Change Broker
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BrokerSelectionPage;

